/* eslint-disable import/no-cycle */
import { IAddress } from './Address';
import { IJob } from './Job';
import { IOrganization } from './Organization';
import { ISiteRolePolicy, ISiteWithholdings } from './Site';
import { ISubmittedBy, IUserWithDetails } from './User';

export enum DisbursementMethod {
  CASH = 'cash',
  INSTANT = 'instant',
  PAYROLL = 'payroll',
}

export enum EmployeeActionRequiredType {
  EMPLOYER_APPROVAL = 'employer_approval',
  INVITE_EXPIRED = 'invite_expired',
  INVITE_REQUIRED = 'invite_required',
  INVITE_SEND_FAILURE = 'invite_send_failure',
  NONE = 'null',
}

enum EmployeeApprovalValueFormat {
  EMAIL = 'email',
  MOBILE = 'mobile_number',
  STRING = 'string',
}

export enum EmployeeStatus {
  ACTION_REQUIRED = 'action_required',
  APPROVED = 'approved',
  DISMISSED = 'dismissed',
  INVITED = 'invited',
}

export enum EmployeeType {
  ADMIN = 'admin',
  USER = 'user',
}

export enum EventActionTypes {
  ACCEPT_INVITE = 'accept_invite',
  CREATE = 'create',
  DELETE = 'delete',
  UPDATE = 'update',
}

enum MatchConditions {
  EXACT = 'exact',
  FUZZY = 'fuzzy',
}

export enum SiteRole {
  ADMIN = 'admin',
  EMPLOYEE = 'employee',
  MANAGER = 'manager',
}

export enum WithholdingsType {
  CUSTOM = 'custom',
  DEFAULT = 'default',
  NONE = 'none',
}

interface IEmployeeApprovalFailure {
  employeeValue: string;
  field: string;
  matchCondition: MatchConditions;
  userValue?: string;
  valueFormat: EmployeeApprovalValueFormat;
}

export interface IBaseEmployee {
  id: string;
  name: string;
  type: EmployeeType;
  status: EmployeeStatus;
  approvalFailures?: IEmployeeApprovalFailure[];
  integrations?: IEmployeeIntegration[];
  organization?: IOrganization;
  sites: IEmployeeSite[];
  user?: IUserWithDetails;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
}

export interface IEmployee extends IBaseEmployee {
  actionRequired?: EmployeeActionRequiredType;
  dismissReason?: string;
  firstName: string;
  lastName: string;
  inviteRecipient?: string;
  statusHistory?: IEmployeeStatusHistory[];
}

export interface IEmployeeIntegration {
  id: string;
  siteId: string;
  type: string;
}

export interface IEmployeeSiteWithholdings extends ISiteWithholdings {
  type: WithholdingsType;
}

export interface IEmployeeSiteSettings {
  tipCalculator: boolean;
  disbursementMethod: DisbursementMethod;
  requireEmployeeId: boolean;
  rolePolicy?: ISiteRolePolicy;
}

export interface IEmployeeSite {
  address?: IAddress;
  disbursementMethod: DisbursementMethod;
  dismissReason?: string;
  employeeDisplayName?: string;
  employeeId?: string;
  id: string;
  job: IJob;
  name: string;
  legalName?: string;
  role: SiteRole;
  settings?: IEmployeeSiteSettings;
  salesForce?: string;
  status: EmployeeStatus;
  createdAt?: string;
  updatedAt?: string;
  withholdings?: IEmployeeSiteWithholdings;
}

interface IEmployeeStatusHistory {
  action: EventActionTypes;
  changedBy: ISubmittedBy;
  status: EmployeeStatus;
  createdAt: string;
}

interface IPermission {
  status: PermissionStatus;
  sites: string[];
}

export interface ICreateEmployee {
  firstName: string;
  lastName: string;
  type: EmployeeType;
  inviteRecipient: string;
  organizationId: string;
  sites?: ICreateEmployeeSite[];
  integrations?: IEmployeeIntegration[];
}

export interface IUpdateEmployee {
  firstName?: string;
  lastName?: string;
  inviteRecipient?: string;
  status?: EmployeeStatus;
  type?: EmployeeType;
  dismissReason?: string;
  integrations?: IEmployeeIntegration[];
  permissions?: {
    paymentApproval?: IPermission;
  };
}

export interface ICreateEmployeeSite {
  id: string;
  role: SiteRole;
  job?: IJob;
  disbursementMethod?: DisbursementMethod;
  employeeDisplayName?: string;
  employeeId?: string;
}

export interface IUpdateEmployeeSite {
  role?: SiteRole;
  job?: IJob;
  status?: EmployeeStatus;
  disbursementMethod?: DisbursementMethod;
  employeeDisplayName?: string;
  employeeId?: string;
  dismissReason?: string;
  withholdings?: IEmployeeSiteWithholdings;
}

export enum AffiliationType {
  SITES = 'Sites',
  ORGANIZATIONS = 'Organizations',
}

export enum EmployeeReviewFieldKey {
  FIRST_NAME = 'firstName',
  LAST_NAME = 'lastName',
  DATE_OF_BIRTH = 'dateOfBirth',
  IDENTITY_RECIPIENT = 'invite_recipient',
}

export interface IEmployeeReviewField {
  user: string;
  employee: string;
}

export enum EmployeeRole {
  ADMIN = 'admin',
  EMPLOYEE = 'employee',
  MANAGER = 'manager',
  SITE_ADMIN = 'siteadmin',
}

export interface IPayeeEmployee {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  employeeId?: string;
  job: { title: string };
  type: EmployeeType;
  role: SiteRole;
  disbursementMethod: DisbursementMethod;
  user?: IUserWithDetails;
  withholdings?: IEmployeeSiteWithholdings;
}

export interface IPayeeTotals {
  gross: number;
  net: number;
  cash: number;
  withholdingAmount: number;
  holdbackAmount: number;
  tipAmount?: number;
  autoGratuity?: number;
}

export interface IPayee extends IPayeeTotals {
  id: string;
  name: string;
  employee?: IPayeeEmployee;
  note?: string;
}

export enum OnboardingEmployeeStatus {
  CONSTRAINT_VIOLATION = 'constraint_violation',
  CONFLICT = 'conflict',
  EXIST = 'exist',
  NEW = 'new',
  LINK = 'link',
  REACTIVATE = 'reactivate',
  REACTIVATE_SITE = 'reactivate_site',
  SKIP = 'skip',
  ADD_SITE_ROLE = 'add_site_role',
  INELIGIBLE = 'ineligible',
}

// Action the backend executor actually performed for a candidate during processing.
export enum OnboardingEmployeeResultAction {
  CREATED = 'created',
  LINKED = 'linked',
  REACTIVATED = 'reactivated',
  REACTIVATED_SITE = 'reactivated_site',
  ADDED_SITE_ROLE = 'added_site_role',
  SKIPPED = 'skipped',
  DEFERRED = 'deferred',
  ERRORED = 'errored',
}

export type OnboardingEmployeeResultOutcome = 'success' | 'noop' | 'error';

export interface IOnboardingEmployeeResult {
  action: OnboardingEmployeeResultAction;
  outcome: OnboardingEmployeeResultOutcome;
  reason?: string;
}

export interface IOnboardingEmployee {
  disbursementMethod: DisbursementMethod;
  firstName: string;
  lastName: string;
  inviteRecipient: string;
  role: SiteRole;
  employeeId: string;
  providerId: string;
  job: { title: string };
  onboard: { status: OnboardingEmployeeStatus; reason?: string; employeeId?: string; employeeIds?: string[] };
}

export interface IOnboardingEmployeeList {
  employees: IOnboardingEmployee[];
  provider: { type: string };
}

// An onboarding candidate decorated by the backend executor with the outcome of processing it.
export interface IProcessedOnboardingEmployee extends IOnboardingEmployee {
  result: IOnboardingEmployeeResult;
}

// Un-enveloped success body of POST /v1/sites/:site_id/pos/onboarding.
export interface IProcessOnboardingResponse {
  employees: IProcessedOnboardingEmployee[];
}

// UI summary buckets derived from the decorated response, grouped by onboarding status.
export interface IProcessOnboardingEmployeesResponse {
  toCreate: IOnboardingEmployee[];
  toLink: IOnboardingEmployee[];
  toReactivate: IOnboardingEmployee[];
  toReactivateSite: IOnboardingEmployee[];
  toSkip: IOnboardingEmployee[];
  addSiteRole: IOnboardingEmployee[];
  ineligible: IOnboardingEmployee[];
}
